import React, { useMemo, useState } from 'react';
import { Card, Input, Select, Space, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EyeOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useGetQaqcPipeHeatno } from '../../../../hooks/genericHooks';
import StandardTable from '../../../../components/common/StandardTable';
import type { QaqcPipeHeatno } from '../../../../types';
import Button from '../../../../components/common/Button';

const QaqcPipeHeatnoList: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetQaqcPipeHeatno();
  const rows: QaqcPipeHeatno[] = Array.isArray(data?.data) ? (data!.data as unknown as QaqcPipeHeatno[]) : [];

  // Filters
  const [filterComponent, setFilterComponent] = useState<string | undefined>();
  const [filterManufacture, setFilterManufacture] = useState<string | undefined>();
  const [searchHeat, setSearchHeat] = useState<string>('');

  const compOptions = useMemo(() => Array.from(new Set(rows.map(r => r.component_name || '')))
    .filter(Boolean).sort().map(v => ({ value: v, label: v })), [rows]);
  const manuOptions = useMemo(() => Array.from(new Set(rows.map(r => r.manufacture || '')))
    .filter(Boolean).sort().map(v => ({ value: v, label: v })), [rows]);

  const filtered = useMemo(() => {
    const term = searchHeat.trim().toLowerCase();
    return rows.filter(r => {
      if (filterComponent && (r.component_name || '') !== filterComponent) return false;
      if (filterManufacture && (r.manufacture || '') !== filterManufacture) return false;
      if (term) {
        const h = String(r.heat_no || '').toLowerCase();
        if (!h.includes(term)) return false;
      }
      return true;
    });
  }, [rows, filterComponent, filterManufacture, searchHeat]);

  const columns = useMemo(
    () => [
      { title: 'Component', dataIndex: 'component_name', key: 'component_name' },
      { title: 'Heat No', dataIndex: 'heat_no', key: 'heat_no' },
      { title: 'Manufacture', dataIndex: 'manufacture', key: 'manufacture' },
      { title: 'PMI Ref', dataIndex: 'pmi_ref_no', key: 'pmi_ref_no' },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        fixed: 'right',
        render: (_: any, rec: QaqcPipeHeatno) => (
          <Space>
            <Tooltip title="View">
              <Button action="read" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/qaqc/piping/operations/heatnos/view/${rec.id}`, { state: { record: rec } })} />
            </Tooltip>
            <Tooltip title="Edit">
              <Button action="edit" type="primary" size="small" icon={<EditOutlined />} onClick={() => navigate(`/qaqc/piping/operations/heatnos/edit/${rec.id}`, { state: { record: rec } })} />
            </Tooltip>
          </Space>
        )
      }
    ],
    []
  );

  return (
    <Card
      title="Heat Numbers"
      extra={
        <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qaqc/piping/operations/heatnos/create')}>
          New Heat No
        </Button>
      }
    >
      {error ? (
        <div style={{ color: 'red', marginBottom: 12 }}>Failed to load Heat Numbers: {String((error as any)?.message || error)}</div>
      ) : null}
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          showSearch
          placeholder="Filter by Component"
          style={{ width: 220 }}
          options={compOptions}
          value={filterComponent}
          onChange={setFilterComponent}
          optionFilterProp="label"
        />
        <Select
          allowClear
          showSearch
          placeholder="Filter by Manufacture"
          style={{ width: 220 }}
          options={manuOptions}
          value={filterManufacture}
          onChange={setFilterManufacture}
          optionFilterProp="label"
        />
        <Input
          placeholder="Search Heat No"
          style={{ width: 220 }}
          value={searchHeat}
          onChange={(e) => setSearchHeat(e.target.value)}
        />
        <Button icon={<ReloadOutlined />} onClick={() => { setFilterComponent(undefined); setFilterManufacture(undefined); setSearchHeat(''); }}>
          Reset Filters
        </Button>
      </Space>
      <StandardTable
        loading={isLoading}
        columns={columns as any}
        dataSource={filtered}
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </Card>
  );
};

export default QaqcPipeHeatnoList;
